import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, CircleNotch, EnvelopeSimple, Trash, ArrowsClockwise, Copy, Fire } from "@phosphor-icons/react";
import HelpTip from "@/components/onboarding/HelpTip";

const STAGES = [
  { id: "new", label: "New" },
  { id: "qualified", label: "Qualified" },
  { id: "demo", label: "Demo" },
  { id: "proposal", label: "Proposal" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

const scoreColor = (s) => (s >= 75 ? "var(--success)" : s >= 50 ? "var(--warning)" : "var(--danger)");

export default function Conversion() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", seats: 1, interest: "monthly", notes: "" });
  const [saving, setSaving] = useState(false);
  const [emailLead, setEmailLead] = useState(null);
  const [emailText, setEmailText] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailGoal, setEmailGoal] = useState("book a demo");

  const load = () => api.listLeads().then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addLead = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.createLead({ ...form, seats: Number(form.seats) });
      toast.success("Lead added & AI-qualified");
      setOpen(false);
      setForm({ name: "", company: "", email: "", phone: "", seats: 1, interest: "monthly", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const move = async (lead, stage) => {
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, stage } : l)));
    try { await api.updateStage(lead.id, stage); } catch { toast.error("Update failed"); load(); }
  };

  const del = async (id) => { await api.deleteLead(id); load(); toast.success("Lead removed"); };

  const openEmail = async (lead) => {
    setEmailLead(lead); setEmailText(""); setEmailLoading(true);
    try {
      const r = await api.draftEmail(lead.id, emailGoal);
      setEmailText(r.data.email);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setEmailLoading(false); }
  };

  const regenEmail = async () => {
    setEmailLoading(true);
    try { const r = await api.draftEmail(emailLead.id, emailGoal); setEmailText(r.data.email); }
    catch (e) { toast.error("Failed"); } finally { setEmailLoading(false); }
  };

  const syncZoho = async (lead) => {
    toast.promise(api.zohoSync(lead.id).then(() => load()), {
      loading: "Syncing to Zoho…",
      success: "Synced to Zoho CRM",
      error: (e) => e.response?.data?.detail || "Zoho sync failed",
    });
  };

  return (
    <div className="fade-up space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm flex items-center" style={{ color: "var(--text-2)" }}>{leads.length} leads · AI-qualified & Zoho-ready<HelpTip text="Every lead you add is automatically scored 0-100 by the AI based on fit and buying signals. 75+ (marked with a flame) are your hottest leads -- follow up on those first." /></p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-lead-button" className="px-5 py-2.5 text-sm font-bold flex items-center gap-2 text-white hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
              <Plus size={16} weight="bold" /> Add Lead
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--surface)] border-[var(--line)] text-[var(--text)]">
            <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Full name *"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"]].map(([k, label]) => (
                <input key={k} data-testid={`lead-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={label} className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
              ))}
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="lead-seats" type="number" min="1" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} placeholder="Seats" className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }} />
                <select data-testid="lead-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
                  <option value="monthly">Monthly (month-to-month)</option>
                  <option value="license">License (1-3 yr, 50+ seats)</option>
                </select>
              </div>
              <textarea data-testid="lead-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / requirements" rows={3} className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none resize-none" style={{ borderColor: "var(--line)" }} />
              <button data-testid="save-lead-button" onClick={addLead} disabled={saving} className="w-full px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60" style={{ background: "var(--primary)" }}>
                {saving ? <CircleNotch size={16} className="animate-spin" /> : null} {saving ? "Qualifying…" : "Add & Qualify"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {STAGES.map((st) => {
          const items = leads.filter((l) => l.stage === st.id);
          return (
            <div key={st.id} data-testid={`stage-col-${st.id}`} className="border bg-[var(--surface)] flex flex-col" style={{ borderColor: "var(--line)" }}>
              <div className="px-3 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--line)" }}>
                <span className="text-xs font-bold uppercase tracking-wider">{st.label}</span>
                <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{items.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {items.map((l) => (
                  <div key={l.id} data-testid="lead-card" className="border p-3 bg-[#0b1220] hover:border-[var(--accent)] transition-colors" style={{ borderColor: "var(--line)" }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{l.name}</div>
                        <div className="text-xs truncate" style={{ color: "var(--text-2)" }}>{l.company || "—"}</div>
                      </div>
                      <span className="mono text-xs font-bold flex items-center gap-0.5" style={{ color: scoreColor(l.score) }}>
                        {l.score >= 75 && <Fire size={11} weight="fill" />}{l.score}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 mono text-[10px]" style={{ color: "var(--text-2)" }}>
                      <span className="px-1.5 py-0.5 border" style={{ borderColor: "var(--line)" }}>{l.seats} seat{l.seats > 1 ? "s" : ""}</span>
                      <span className="px-1.5 py-0.5 border" style={{ borderColor: "var(--line)", color: l.interest === "license" ? "var(--accent)" : "var(--text-2)" }}>{l.interest}</span>
                      {l.zoho_id && <span style={{ color: "var(--success)" }}>✓zoho</span>}
                    </div>
                    <select value={l.stage} onChange={(e) => move(l, e.target.value)} data-testid="lead-stage-select" className="w-full mt-2 text-[11px] mono px-2 py-1 border bg-[var(--surface)] outline-none" style={{ borderColor: "var(--line)" }}>
                      {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <div className="flex items-center gap-1 mt-2">
                      <button data-testid="email-lead-button" onClick={() => openEmail(l)} title="Draft email" className="flex-1 py-1.5 border flex items-center justify-center hover:bg-[#111827] transition-colors" style={{ borderColor: "var(--line)" }}><EnvelopeSimple size={14} /></button>
                      <button data-testid="zoho-sync-button" onClick={() => syncZoho(l)} title="Sync to Zoho" className="flex-1 py-1.5 border flex items-center justify-center hover:bg-[#111827] transition-colors" style={{ borderColor: "var(--line)" }}><ArrowsClockwise size={14} /></button>
                      <button data-testid="delete-lead-button" onClick={() => del(l.id)} title="Delete" className="flex-1 py-1.5 border flex items-center justify-center hover:bg-[#111827] transition-colors" style={{ borderColor: "var(--line)", color: "var(--danger)" }}><Trash size={14} /></button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-center py-4 mono text-[11px]" style={{ color: "var(--text-2)" }}>empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!emailLead} onOpenChange={(o) => !o && setEmailLead(null)}>
        <DialogContent className="bg-[var(--surface)] border-[var(--line)] text-[var(--text)] max-w-2xl">
          <DialogHeader><DialogTitle>AI Email Draft — {emailLead?.name}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <select value={emailGoal} onChange={(e) => setEmailGoal(e.target.value)} data-testid="email-goal-select" className="text-sm px-3 py-2 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
              {["book a demo", "follow up", "send pricing", "re-engage cold lead", "close the deal"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <button data-testid="regen-email-button" onClick={regenEmail} disabled={emailLoading} className="text-xs flex items-center gap-1 px-3 py-2 border hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)" }}><ArrowsClockwise size={13} /> Regenerate</button>
            {emailText && <button onClick={() => { navigator.clipboard.writeText(emailText); toast.success("Copied"); }} className="text-xs flex items-center gap-1 px-3 py-2 border hover:bg-[#0b1220] transition-colors ml-auto" style={{ borderColor: "var(--line)" }}><Copy size={13} /> Copy</button>}
          </div>
          <div className="border p-4 bg-[#0b1220] min-h-[240px] max-h-[50vh] overflow-auto" style={{ borderColor: "var(--line)" }} data-testid="email-draft-output">
            {emailLoading ? (
              <div className="text-center py-16"><CircleNotch size={26} className="animate-spin mx-auto mb-2" color="var(--accent)" /><p className="mono text-sm" style={{ color: "var(--text-2)" }}>Drafting…</p></div>
            ) : (
              <div className="prose-cs text-sm"><ReactMarkdown>{emailText}</ReactMarkdown></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
